import React, { useEffect } from 'react';
import styled from 'styled-components';
import DotLoader from '@svg/dot_loader';

import { Stack } from '@mui/material';
import { NodeIcon, NodeId, getIcon } from '../Defaults/NodeHeader';
import { Command, tabActions } from '@store/slices';
import { Button, LoadingButton } from '@common/Button';
import { DoNotDisturbAltOutlined, PlayArrowOutlined, StopOutlined, VisibilityOutlined } from '@mui/icons-material';
import { useDispatch, useSelector } from 'react-redux';
import { AppDispatch, flowActions, State } from '@store/index';
import { useTheme } from '../../../styles';
import { useParams } from 'react-router-dom';
import { useTranslate } from '@common/hooks/useTranslate';
import { useSnackbar } from 'notistack';
import { runWorkflowHandler } from './utils';
import { RTC } from '@components/Flow/Nodes/Trigger/InboundCall/Softphone/useRTC';
import { NodeVersion } from './NodeSidebarHeader';
import { useEntityExecution } from '@common/hooks/useEntityExecution';

export const NodeSidebarHeaderContainer = styled.div<{ backgroundColor: string }>`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0 ${({ theme }) => theme.spacing(3)};
  color: ${({ theme }) => theme.text.standard};
  position: sticky;
  top: 0;
  height: 60px;
  z-index: 100;
  font-weight: 500;
  background: ${({ theme }) => theme.background.standard};
  border-bottom: 1px solid ${({ theme }) => theme.line.light};

  #hideable {
    margin-right: 4px;
  }

  #hideable-run {
    margin-left: 4px;
  }

  @media (max-width: 1950px) {
    #hideable {
      display: none;
    }
  }

  @media (max-width: 1500px) {
    #hideable-run {
      display: none;
    }
  }

  @media (max-width: 1700px) {
    #execution-title {
      max-width: 90px;
      white-space: nowrap;
      overflow: hidden;
      text-overflow: ellipsis;
    }
  }
`;

export enum WorkflowExecutionType {
  Request = 'request',
  InboundCall = 'voice',
  InboundMessage = 'messaging',
  Module = 'module',
}

export type WorkflowExecution = {
  tab?: string;
  setTab?: React.Dispatch<string>;
  abortControllerRef: React.MutableRefObject<AbortController | null>;
  type: WorkflowExecutionType;
  timer?: {
    minutes: number;
    seconds: number;
    start: () => void;
    pause: () => void;
    reset: () => void;
  };
};

interface Props {
  workflowExecution: WorkflowExecution;
  command: Command;
  rtc?: RTC;
}

export const NodeExecutionHeader: React.FC<Props> = ({ workflowExecution, command, rtc }) => {
  const theme = useTheme();
  const dispatch = useDispatch<AppDispatch>();

  const { service_id, workflow_id } = useParams();
  const { enqueueSnackbar } = useSnackbar();
  const { translate } = useTranslate();
  const { isExecutionDisabled } = useEntityExecution(workflowExecution.type, rtc);

  const disableExecution = isExecutionDisabled();

  const commands = useSelector((state: State) => state.tabs.active.commands);
  const nodeErrors = useSelector((state: State) => state.tabs.active.nodeErrors);
  const trace = useSelector((state: State) => state.tabs.active.run);
  const version = command?.config?.version;

  const isInboundCall = workflowExecution.type === WorkflowExecutionType.InboundCall;
  const isModule = workflowExecution.type === WorkflowExecutionType.Module;
  const workflowType = isModule ? 'Module' : 'Workflow';
  const isTraceRunning = trace?.id || trace?.running;
  const stopWorkflowDisabled = Boolean(isTraceRunning && isInboundCall && (!rtc?.call || rtc?.state?.isInitiating));

  const { timer, tab, setTab, abortControllerRef } = workflowExecution;
  const { background, title, icon } = command.config;

  const hasFieldErrors = nodeErrors.some(
    (nodeError) => nodeError?.fields?.length > 0 && nodeError.workflowId === workflow_id
  );

  const completionNodes = commands.filter((command) => command.config.command_id === 'llm.completion');
  const isMessageArray = Boolean(
    completionNodes.find(
      (node) => node.state?.message_array?.enabled && node?.state?.messages?.type !== 'message_expression'
    )
  );

  useEffect(() => {
    return () => {
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
    };
  }, []);

  const openChangelog = () => {
    dispatch(flowActions.setShowNodeChangelog(command.config));
  };

  const run = async () => {
    // reset the call data before starting a new call
    if (rtc) rtc.setCallData({ call: undefined });

    try {
      await runWorkflowHandler({
        service_id,
        workflow_id,
        translate,
        dispatch,
        workflowExecution,
        isMessageArray,
        enqueueSnackbar,
        rtc,
      });
    } catch (e) {}
  };

  const clear = () => {
    if (hasFieldErrors) {
      const fieldErrors = nodeErrors.filter(
        (nodeError) => nodeError?.fields?.length > 0 && nodeError.workflowId === workflow_id
      );

      const firstError = fieldErrors[0];
      dispatch(tabActions.setSelectedNode(firstError.id));
    }

    // terminate the call if still active
    if (rtc?.call) {
      try {
        if (typeof rtc?.call?.hangup === 'function') rtc?.call?.hangup();
      } catch (e) {}
    }

    if (tab === '3' && setTab) setTab('1');
    dispatch(tabActions.clearWorkflowRun());
  };

  const stop = () => {
    if (abortControllerRef.current) {
      abortControllerRef.current.abort('MANUAL_ABORT');
    }

    // terminate the call if still active
    if (rtc?.call) {
      try {
        if (typeof rtc?.call?.hangup === 'function') rtc?.call?.hangup();
      } catch (e) {}
    }

    enqueueSnackbar(`${workflowType} Aborted`, { variant: 'warning', autoHideDuration: 3000 });
    dispatch(tabActions.cancelWorkflowRun());
    if (!trace?.id) clear();
  };

  return (
    <NodeSidebarHeaderContainer backgroundColor={background || ''}>
      <Stack direction='row' width='100%' justifyContent='space-between' alignItems='center'>
        {!(trace?.id || trace?.running) && (
          <Stack direction='row' spacing={2} alignItems='center'>
            <NodeIcon background={background}>{getIcon(icon)}</NodeIcon>
            <p id='execution-title'>{title}</p>
          </Stack>
        )}
        {(trace?.id || trace?.running) && (
          <Stack direction='row' spacing={2} alignItems='center'>
            {!trace?.running && trace?.id && <NodeIcon background={theme.telnyx.yellow}>{getIcon('run')}</NodeIcon>}
            {trace.running && (
              <NodeIcon background={theme.primary}>
                <DotLoader height={4} />
              </NodeIcon>
            )}
            <p>
              <span id='hideable'>{workflowType}</span>
              Run
            </p>
          </Stack>
        )}
        <Stack direction='row' spacing={1} alignItems='center'>
          {(trace?.running || trace?.id) && timer && (
            <NodeId timer background={theme.telnyx.purple}>
              {timer?.minutes > 0 ? `${timer.minutes}m ${timer.seconds}s` : `${timer.seconds}s`}
            </NodeId>
          )}
          {trace?.running ? (
            <Button small customColor='red' startIcon={<StopOutlined />} onClick={stop} disabled={stopWorkflowDisabled}>
              <p>
                Stop
                <span id='hideable-run'>{workflowType}</span>
              </p>
            </Button>
          ) : trace?.id || trace?.error ? (
            <Button
              small
              customColor={hasFieldErrors ? 'red' : 'yellow'}
              startIcon={hasFieldErrors ? <VisibilityOutlined /> : <DoNotDisturbAltOutlined />}
              onClick={clear}
            >
              {hasFieldErrors ? (
                'View Errors'
              ) : (
                <p>
                  Clear
                  <span id='hideable-run'>Execution</span>
                </p>
              )}
            </Button>
          ) : (
            <LoadingButton
              minWidth='85px'
              loading={Boolean(trace?.running)}
              small
              startIcon={<PlayArrowOutlined />}
              onClick={run}
              customColor='green'
              disabled={disableExecution}
            >
              <p>
                Run
                <span id='hideable-run'>{workflowType}</span>
              </p>
            </LoadingButton>
          )}
          <NodeId background={background}>#{command?.node?.id}</NodeId>
          {version && !(trace?.running || trace?.id) && <NodeVersion onClick={openChangelog}>v{version}</NodeVersion>}
        </Stack>
      </Stack>
    </NodeSidebarHeaderContainer>
  );
};
